import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { CollapsibleCard, Textarea } from '@insolech/ui';
import { productQueries } from '@insolech/sdk';
import { supabase } from '@/lib/supabase';
import { PrescriptionAssistantPanel } from './PrescriptionAssistantPanel';
import type { PatientPickerValue, ReorderPrefill } from './PatientPicker';
import { PatientSection } from './prescription/PatientSection';
import { InsoleDesignSection } from './prescription/InsoleDesignSection';
import { RearfootSection } from './prescription/RearfootSection';
import { MidfootSection } from './prescription/MidfootSection';
import { ForefootSection } from './prescription/ForefootSection';
import { OffloadingsSection } from './prescription/OffloadingsSection';
import { SandalStyleSection } from './prescription/SandalStyleSection';
import { SECTION_OF_CARD, type SectionKey } from './prescription/cardIds';
import { collectUnavailableValues, indexUnavailable } from './prescriptionLegality';
import type { PrescriptionDeepState } from './prescription/prescriptionState';
// Types + default state live in a sibling module so tests can import
// them without dragging in this file's eager `@/lib/supabase` import.
// Re-exported from this module for back-compat with existing call sites.
export {
  DEFAULT_PRESCRIPTION_STATE,
  prescriptionHasOperatorContent,
  type PrescriptionFormState,
  type PrescriptionModification,
} from './prescriptionFormDefaults';
import {
  prescriptionHasOperatorContent,
  type PrescriptionFormState,
} from './prescriptionFormDefaults';
import { deepForLineType } from './deepForLineType';
import { designForMaterial } from './designForMaterial';

/**
 * Extended state shape with Phase 4c measurements. The wizard's marshal
 * function (`buildLineConfiguration`) already passes these through into
 * the line.configuration jsonb, so the SDK contract is unchanged.
 */
interface PrescriptionFormStateExt extends PrescriptionFormState {
  arch_height_left_mm?: number;
  arch_height_right_mm?: number;
}

interface Props {
  value: PrescriptionFormState;
  /**
   * Accepts either a concrete next value OR a functional updater
   * `(prev) => next`. The updater form is critical: child sections
   * (InsoleDesign, Rearfoot, Forefoot…) run their mount-init useEffects
   * in the same commit. Concrete values would close over a stale `value`
   * snapshot, and the second effect's patch would overwrite the first
   * (e.g. seeded insole_cup wiped by seeded rearfoot_posting). Updaters
   * always see the latest committed state via React's setState chain.
   */
  onChange: React.Dispatch<React.SetStateAction<PrescriptionFormState>>;
  /** When true, render fields as read-only text (used by "View full prescription" drawer). */
  readOnly?: boolean;
  /**
   * The visual product family the operator picked in the Subtype step. This
   * routes the form to the right variant - sandals get the orthopaedic-sandal
   * fields, insoles share the custom-insole template with material/thickness
   * defaults adjusted per family.
   */
  subtype?:
    | 'carbon_insole'
    | 'pa12_insole'
    | 'tpu_insole'
    | 'eva_insole'
    | 'polypropylene_insole'
    | 'fibreglass_insole'
    | 'orthopaedic_sandal'
    | 'insoles_refurbishment'
    | undefined;
  /** Patient weight (kg) from the patient step - drives default shell thickness. */
  patientWeightKg?: number | null | undefined;
  /** Explicit patient name for the Insole Design "copy patient name" checkbox.
   *  The wizard derives this from its patient section; surfaces (like the Edit
   *  Prescription dialog) that don't mount the full patient section pass the
   *  name directly. Takes precedence over the patient-prop-derived name. */
  patientName?: string | null | undefined;
  /**
   * R32 - patient state now lives inline as the first section of the
   * prescription form. The wizard still owns the data (so save-as-draft
   * and unsaved-changes guards see it) and passes it through these props.
   * `undefined` means the caller doesn't want the patient section
   * (read-only viewers, view-full-prescription drawer, etc.).
   */
  patient?: PatientPickerValue | undefined;
  onPatientChange?: ((next: PatientPickerValue) => void) | undefined;
  /** Forwarded to the patient section so picking a past order (reorder-by-
   *  name) can prefill patient + prescription atomically at the wizard. */
  onReorderPrefill?: ((next: ReorderPrefill) => void) | undefined;
  showPatientErrors?: boolean | undefined;
  /** R35 — explicit clinic id for the patient-section dupe/search lookups.
   *  Threaded by the wizard so multi-clinic users get clinic-scoped
   *  results matching the order's chosen clinic. */
  patientClinicId?: string | null | undefined;
  /** The order's own clinic. Unlike patientClinicId this is NOT widened to null
   *  for admins, so adopting a patient record stays confined to this order's
   *  clinic even when the search deliberately spans all of them. */
  patientOrderClinicId?: string | null | undefined;
  /** When true, modification cards highlight required-but-empty fields
   *  (Heel raise heights, Arch height heights). Set by the wizard after
   *  the user clicks Continue and validation finds a missing value. */
  showPrescriptionErrors?: boolean | undefined;
  /** R33 - controlled by the wizard so the open button can sit next to
   *  the stepper card when the assistant is hidden. */
  assistantCollapsed?: boolean;
  onAssistantCollapsedChange?: ((next: boolean) => void) | undefined;
}

/**
 * Phase 4c: the modification catalog now lives at
 * `forms/modificationCatalog.ts`. The catalog defines per-modification
 * parameters (angles, depths, sides) so the form can render rich,
 * legacy-app-equivalent inputs instead of the simple checkbox + intensity
 * pill from Phase 4b.
 */

/**

/**
 * Map a visual subtype to the canonical `line_type` enum value used to filter
 * the products catalog. PA12 is a 3D-printed material so it maps to the
 * `premium_3d_insole` family; the rest map straight through.
 */
function lineTypeForSubtype(
  subtype: NonNullable<Props['subtype']>,
): PrescriptionFormState['line_type'] {
  if (subtype === 'orthopaedic_sandal') return 'orthopedic_sandal';
  if (subtype === 'pa12_insole') return 'premium_3d_insole';
  return 'custom_insole';
}

export function PrescriptionForm({
  value,
  onChange,
  readOnly,
  subtype,
  patientWeightKg,
  patientName,
  patient,
  onPatientChange,
  onReorderPrefill,
  showPatientErrors,
  patientClinicId,
  patientOrderClinicId,
  showPrescriptionErrors,
  assistantCollapsed = false,
  onAssistantCollapsedChange,
}: Props) {
  const { t } = useTranslation('orders');

  // Phase 4b iter round 2 (Fix B): when a subtype was selected in Step 2, we
  // narrow the product catalog to that line_type so the auto-pick is unambiguous.
  // When no subtype is provided (e.g. read-only legacy data), fall back to the
  // full custom family so the existing dropdown UX still works.
  const subtypeLineType = subtype ? lineTypeForSubtype(subtype) : null;
  const productsQuery = useQuery({
    ...(subtypeLineType
      ? productQueries.productsListQuery(supabase, { lineType: subtypeLineType })
      : productQueries.productsListQuery(supabase, { family: 'custom' })),
  });

  const update = (patch: Partial<PrescriptionFormState>) =>
    onChange((prev) => ({ ...prev, ...patch }));

  // When subtype is provided, default the material to match.
  React.useEffect(() => {
    if (!subtype || readOnly) return;
    if (value.material) return; // don't overwrite user-set material
    const defaultMaterial =
      subtype === 'carbon_insole'
        ? 'Carbon'
        : subtype === 'pa12_insole'
          ? 'PA12'
          : subtype === 'tpu_insole'
            ? 'TPU'
            : subtype === 'eva_insole'
              ? 'EVA'
              : subtype === 'polypropylene_insole'
                ? 'Polypropylene'
                : subtype === 'fibreglass_insole'
                  ? 'Fibreglass'
                  : undefined;
    if (defaultMaterial && defaultMaterial !== value.material) {
      update({ material: defaultMaterial });
    }
    // Intentionally subtype-driven: this is a one-shot default seed when the
    // operator picks a family. Including `value.material` would re-fire every
    // keystroke and could re-seed after the user clears the field; including
    // `update`/`readOnly` would re-run on every parent render (update is a new
    // fn each render). The guards above already handle readOnly + user-set.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subtype]);

  // Auto-pick product_id from subtype (Fix B). Once products load, if the user
  // hasn't already chosen one, set the first matching product. Re-runs when
  // subtype changes (e.g. user goes back and picks a different family).
  React.useEffect(() => {
    if (!subtype || readOnly) return;
    if (!productsQuery.data) return;
    if (productsQuery.data.length === 0) return;
    const target = productsQuery.data[0];
    if (!target) return;
    if (value.product_id === target.id) return;
    // Only auto-pick if the current product_id isn't valid for this subtype's
    // catalog - this allows the user to override the selection without us
    // stomping their choice.
    const currentIsValid =
      value.product_id && productsQuery.data.some((p) => p.id === value.product_id);
    if (currentIsValid) return;
    update({
      product_id: target.id,
      line_type: target.line_type as PrescriptionFormState['line_type'],
    });
    // We deliberately depend on the data identity + subtype, NOT `value`, to
    // avoid an infinite update loop when the parent rebuilds state: this
    // effect calls `update(...)` which mutates `value.product_id`, which
    // would immediately re-fire the effect and overwrite any user choice.
    // `update`/`readOnly` are also omitted (update is a new fn each render;
    // readOnly is guarded above) so the effect only re-runs when the catalog
    // data identity or subtype changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subtype, productsQuery.data]);

  const isSandal = subtype === 'orthopaedic_sandal' || value.line_type === 'orthopedic_sandal';

  // Insoles refurbishment is a stripped-down flow (length + top cover +
  // refurbishment remarks only). No modification sections, no podiatrist
  // observations, no prescription assistant - the lab is re-covering an
  // existing shell, not designing a fresh prescription.
  const isRefurbishment = subtype === 'insoles_refurbishment';

  // R32 - Prescription assistant (presets + pathology). Enabled on live
  // custom-insole AND sandal flows: the sandal form renders the same
  // Rearfoot / Midfoot / Forefoot / Offloadings sections that the assistant's
  // presets and pathology mappings target, so the patches land somewhere
  // visible. Read-only viewers don't need it. Refurbishment skips it because
  // the modification sections aren't rendered there (the lab is re-covering
  // an existing shell, not designing a fresh prescription), so the patches
  // would have nowhere to land.
  const assistantEnabled = !readOnly && !isRefurbishment;

  // Apply a preset / pathology deep-state to the form.
  const onApplyDeep = (next: PrescriptionDeepState) => {
    onChange((prev) => ({ ...prev, deep: next }));
  };

  // R28 Round 1 - Insole Design (legacy parity).
  // Functional updater so concurrent mount-init effects from sibling
  // sections (InsoleDesign + Rearfoot fire in the same commit) compose
  // against the latest state instead of clobbering via stale-closure
  // `value` snapshots. Each patch merges into prev.deep, never prev=undef.
  const setDeep = React.useCallback(
    (patch: Partial<PrescriptionDeepState>) => {
      onChange((prev) => ({
        ...prev,
        deep: { ...(prev.deep ?? {}), ...patch },
      }));
    },
    [onChange],
  );

  // Hide design section for sandals - Round 6 handles their own form.
  const showInsoleDesign = !isSandal;

  // R33 - section open/closed state lives here so the prescription assistant
  // can force-open a section before scrolling to a card inside it.
  // Defaults: patient + design open; modification sections closed (Rafael
  // wants the form to load compact, expanded only when the operator wants).
  const [sectionsOpen, setSectionsOpen] = React.useState<Record<SectionKey, boolean>>({
    patient: true,
    design: false,
    rearfoot: false,
    midfoot: false,
    forefoot: false,
    offloadings: false,
    sandal: false,
  });
  const setSectionOpen = React.useCallback(
    (key: SectionKey) => (next: boolean) =>
      setSectionsOpen((prev) => ({ ...prev, [key]: next })),
    [],
  );

  // Assistant click-through: open the parent section, then scroll once the
  // card has had a chance to render.
  const onJumpToCard = React.useCallback((cardId: string) => {
    const section = SECTION_OF_CARD[cardId];
    if (section) {
      setSectionsOpen((prev) => (prev[section] ? prev : { ...prev, [section]: true }));
    }
    window.setTimeout(() => {
      const el = document.getElementById(cardId);
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      el.classList.add('ring-2', 'ring-brand-400', 'ring-offset-2');
      window.setTimeout(() => {
        el.classList.remove('ring-2', 'ring-brand-400', 'ring-offset-2');
      }, 1600);
    }, 60);
  }, []);

  // Values the chosen product does not offer.
  //
  // Computed from FORM STATE, never from what happens to be mounted: every
  // modification section is collapsed by default, and CollapsibleCard unmounts
  // its children, so a value copied from another product would otherwise be
  // invisible until someone happened to expand exactly the right card. This
  // banner is the reason the operator does not have to.
  const unavailable = React.useMemo(
    () => collectUnavailableValues(value, subtype ?? null),
    [value, subtype],
  );
  // The banner lists CLOSED-LIST violations only. An entry carrying `message`
  // is a numeric-range field (EVA thickness) whose inline message under the
  // input owns it: the banner's "not available for this product, choose a
  // replacement" wording would contradict that message, and it would flash
  // on every keystroke a draft passes through (7, 2.55) on the way to a valid
  // value. The submit gate (checkUnavailableValues) still returns the message,
  // so nothing is unblocked by leaving it out here.
  const bannerItems = React.useMemo(() => unavailable.filter((u) => !u.message), [unavailable]);
  // Handed to the sections so the offending CONTROL marks itself, not just the
  // banner. Gated on `readOnly` for exactly the reason the banner is: a locked
  // delivered order is not a form to fix, and 64 live lines would light up red
  // inside a drawer where every control is disabled. Read-only viewers keep
  // SelectSm's own local marking, which is unchanged and already shipped.
  const unavailableByKey = React.useMemo(
    () => (readOnly ? undefined : indexUnavailable(unavailable)),
    [readOnly, unavailable],
  );
  const seededFrom =
    patient?.reorderFromOrder?.orderNumber ?? patient?.prescriptionSeededFrom ?? null;

  const formContent = (
    <div className="space-y-4">
      {/* Not in read-only viewers. OrderLinesCard mounts this form with
          readOnly to show a delivered order's prescription, and 64 live lines
          hold a value their product no longer offers (57 carbon shells at the
          retired 2.0mm, plus heel raises). Telling someone to "choose a
          replacement before continuing" inside a drawer headed "Order locked
          for editing", with every control disabled, is the kind of alarm that
          gets a safety check switched off. Those orders are already made. */}
      {!readOnly && bannerItems.length > 0 ? (
        <div
          className="rounded-lg border border-error-500 bg-error-50 p-4"
          role="alert"
          data-testid="unavailable-values-banner"
        >
          <h3 className="text-sm font-semibold text-error-700">
            {bannerItems.length === 1
              ? 'One setting is not available for this product'
              : `${bannerItems.length} settings are not available for this product`}
          </h3>
          <p className="mt-1 text-xs text-text-secondary leading-relaxed">
            {seededFrom
              ? `These came across from order ${seededFrom}, which was for a different product. Nothing is wrong with the original order; this product simply does not offer them.`
              : 'These values are not offered by the product selected for this order.'}{' '}
            Choose a replacement for each before continuing.
          </p>
          <ul className="mt-3 space-y-2">
            {bannerItems.map((u) => (
              <li key={u.key} className="flex items-start gap-3 text-xs">
                <span className="flex-1 text-text-primary leading-relaxed">
                  <span className="font-medium">{u.label}</span>
                  {': '}
                  <span className="text-error-700">{u.valueLabel}</span>
                </span>
                <button
                  type="button"
                  onClick={() => onJumpToCard(u.cardId)}
                  className="shrink-0 rounded-md border border-border-default px-2 py-1 font-medium text-text-primary hover:bg-surface-base focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                >
                  Show me
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {/* R32 - Patient as the first section. Owned by the wizard but
          rendered inline here so podiatrists capture patient +
          prescription on a single step. Hidden in read-only contexts
          (e.g. the View-full-prescription drawer) where the parent
          shows patient details separately. */}
      {patient && onPatientChange ? (
        <PatientSection
          value={patient}
          onChange={onPatientChange}
          showErrors={showPatientErrors}
          open={sectionsOpen.patient}
          onOpenChange={setSectionOpen('patient')}
          {...(patientClinicId !== undefined ? { clinicId: patientClinicId } : {})}
          {...(patientOrderClinicId !== undefined
            ? { orderClinicId: patientOrderClinicId }
            : {})}
          subtype={subtype ?? null}
          // Computed from the live form state, so the prior-order modal knows
          // whether "Start fresh" would discard real work.
          prescriptionHasContent={prescriptionHasOperatorContent(value)}
          {...(onReorderPrefill ? { onReorderPrefill } : {})}
          // The ONE sandal flag (subtype OR line_type spelling), so the patient
          // form hides the shoe-related fields by the same rule the payload
          // builders and the wizard's patient gate use.
          isSandal={isSandal}
        />
      ) : null}

      {/* R28 Rounds 1-6 - legacy prescription form parity. Each section
          is a CollapsibleCard, all collapsed by default per Rafael so
          the step opens compact and the podiatrist expands only what
          they're editing. */}
      {isSandal ? (
        <SandalStyleSection
          value={value.deep?.sandal ?? {}}
          onChange={(sandal) => setDeep({ sandal })}
          readOnly={readOnly}
          defaultOpen={false}
          showErrors={showPrescriptionErrors}
          patientName={
            patientName?.trim() ||
            (patient?.formState.full_name ?? '').trim() ||
            patient?.display_name?.trim() ||
            null
          }
        />
      ) : showInsoleDesign ? (
        <InsoleDesignSection
          value={value.deep?.design ?? {}}
          onChange={(design) => setDeep({ design })}
          subtype={subtype ?? null}
          patientWeightKg={patientWeightKg ?? null}
          patientName={
            patientName?.trim() ||
            (patient?.formState.full_name ?? '').trim() ||
            patient?.display_name?.trim() ||
            null
          }
          readOnly={readOnly}
          showErrors={showPrescriptionErrors}
          unavailable={unavailableByKey}
          open={sectionsOpen.design}
          onOpenChange={setSectionOpen('design')}
          refurbishmentMode={isRefurbishment}
        />
      ) : null}

      {/* R34 - showErrors no longer threaded to Rearfoot / Midfoot /
          Forefoot: their height fields paint red as soon as the parent
          dropdown is set (no Continue click needed).
          Refurbishment hides all modification sections - the lab is
          re-covering an existing shell, so heel raises, arch tweaks,
          forefoot wedges and offloadings already exist on the original
          and aren't re-specified here. */}
      {!isRefurbishment ? (
        <>
          <RearfootSection
            value={value.deep?.rearfoot ?? {}}
            onChange={(rearfoot) => setDeep({ rearfoot })}
            subtype={subtype ?? null}
            isSandal={isSandal}
            readOnly={readOnly}
            unavailable={unavailableByKey}
            open={sectionsOpen.rearfoot}
            onOpenChange={setSectionOpen('rearfoot')}
          />
          <MidfootSection
            value={value.deep?.midfoot ?? {}}
            onChange={(midfoot) => setDeep({ midfoot })}
            isSandal={isSandal}
            readOnly={readOnly}
            open={sectionsOpen.midfoot}
            onOpenChange={setSectionOpen('midfoot')}
          />
          <ForefootSection
            value={value.deep?.forefoot ?? {}}
            onChange={(forefoot) => setDeep({ forefoot })}
            isSandal={isSandal}
            readOnly={readOnly}
            unavailable={unavailableByKey}
            open={sectionsOpen.forefoot}
            onOpenChange={setSectionOpen('forefoot')}
          />
          <OffloadingsSection
            value={value.deep?.offloadings ?? {}}
            onChange={(offloadings) => setDeep({ offloadings })}
            isSandal={isSandal}
            readOnly={readOnly}
            showErrors={showPrescriptionErrors}
            open={sectionsOpen.offloadings}
            onOpenChange={setSectionOpen('offloadings')}
          />
        </>
      ) : null}
      {/* R28 - Legacy sections (Materials & cover, Diagnosis & pathology,
          Foot measurements, old per-region modification checkbox cards,
          and the legacy sandal-specifics card) removed. All of their
          content is now captured by the new collapsible sections above
          (Insole design, Rearfoot, Midfoot, Forefoot, Offloadings,
          Sandal style). */}

      {/* Podiatrist observations - matches the layout / density of the
          other collapsible sections above. Notes go to the order line's
          configuration JSONB. Hidden on refurbishment - the
          "Refurbishment remarks" textarea inside the refurbishment card
          already captures any free-text instructions for the lab. */}
      {!isRefurbishment ? (
        <CollapsibleCard
          title={t('form_section_observations', { defaultValue: 'Podiatrist observations' })}
          subtitle="Notes for the lab: pain points, gait observations, special requests…"
          defaultOpen={false}
        >
          <Textarea
            value={value.observations ?? ''}
            onChange={(e) => update({ observations: e.target.value || undefined })}
            disabled={readOnly}
            placeholder="Notes for the lab…"
            className="min-h-[72px] text-sm"
          />
        </CollapsibleCard>
      ) : null}
    </div>
  );

  // 2-column layout - form on the left, prescription assistant panel on the
  // right (sticky to the viewport). On mobile (<lg) the panel stacks above.
  // When the wizard reports the assistant as collapsed, the form takes the
  // full width and the toggle button is rendered next to the stepper.
  if (!assistantEnabled || assistantCollapsed) {
    return formContent;
  }
  return (
    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_340px] gap-6">
      <div className="min-w-0">{formContent}</div>
      <aside className="min-w-0">
        <PrescriptionAssistantPanel
          form={value}
          onApplyDeep={onApplyDeep}
          collapsed={false}
          onCollapsedChange={(next) => onAssistantCollapsedChange?.(next)}
          onJumpToCard={onJumpToCard}
          isSandal={isSandal}
          // The order's clinic, so an admin (no default clinic on useAuth) can
          // save presets under the clinic the order is for.
          clinicId={patientOrderClinicId ?? null}
        />
      </aside>
    </div>
  );
}
/**
 * Marshal the wizard form state into the line.configuration jsonb shape
 * that `create_order_with_lines` expects. The shape mirrors the Zod schemas
 * in `packages/db/src/schemas/order-line.ts` for forwards-compat with the
 * planned server-side validation.
 */
export function buildLineConfiguration(
  form: PrescriptionFormState,
  /** The line's solution subtype when the caller knows it (the wizard's
   *  state.subtype, the edit dialog's subtype state). It decides the EVA
   *  thickness rule below; `form.material` is only the fallback. */
  opts?: { subtype?: string | null | undefined },
): Record<string, unknown> {
  const ext = form as PrescriptionFormStateExt;
  const base: Record<string, unknown> = {};
  if (form.material) base.material = form.material;
  if (form.thickness_mm != null) base.thickness_mm = form.thickness_mm;
  if (form.cover) base.cover = form.cover;
  if (form.pathology) base.pathology = form.pathology;
  if (form.observations) base.observations = form.observations;
  if (
    form.size_left_length_mm != null ||
    form.size_right_length_mm != null ||
    ext.arch_height_left_mm != null ||
    ext.arch_height_right_mm != null
  ) {
    base.size = {
      left: form.size_left_length_mm
        ? {
            length_mm: form.size_left_length_mm,
            width_mm: form.size_left_width_mm ?? form.size_left_length_mm * 0.4,
            ...(ext.arch_height_left_mm != null
              ? { arch_height_mm: ext.arch_height_left_mm }
              : {}),
          }
        : null,
      right: form.size_right_length_mm
        ? {
            length_mm: form.size_right_length_mm,
            width_mm: form.size_right_width_mm ?? form.size_right_length_mm * 0.4,
            ...(ext.arch_height_right_mm != null
              ? { arch_height_mm: ext.arch_height_right_mm }
              : {}),
          }
        : null,
    };
  }
  if (form.line_type === 'orthopedic_sandal') {
    if (form.upper_style) base.upper_style = form.upper_style;
    if (form.base_color) base.base_color = form.base_color;
    if (form.upper_color) base.upper_color = form.upper_color;
    if (form.upper_straps_size) base.upper_straps_size = form.upper_straps_size;
    if (form.size_eu != null) base.size = { eu: form.size_eu };
  }
  // R28 - fold the deep prescription state (Insole design, modifications,
  // sandal style, etc.) under a single key. JSONB tolerates anything.
  // Not quite anything: a SANDAL does not store the insole design branch the
  // wizard seeds onto every line (deepForLineType.ts). Rearfoot and the rest
  // are kept. This is the one serialiser all three writers call.
  let deep = deepForLineType(form.line_type, form.deep);
  // Likewise, only an EVA line stores the EVA base thickness the wizard seeds
  // onto every design node (designForMaterial.ts). The control renders on the
  // SUBTYPE, so the subtype decides when known; `material` is a cache that can
  // lag a subtype switch and is only the fallback. Still never mutates the form.
  if (deep?.design) {
    const isEva = opts?.subtype ? opts.subtype === 'eva_insole' : form.material === 'EVA';
    const design = designForMaterial(isEva, deep.design);
    if (design !== deep.design) deep = { ...deep, design };
  }
  if (deep && Object.keys(deep).length > 0) {
    base.deep = deep;
  }
  return base;
}
